import React from "react";
import {getUsers} from "../services/UserService";
import {AccountTypeValue} from "../types";
import {AccountType} from "../models/AccountType";
import { formatGuardianDisplayName } from "./guardianDisplayName";

function formatAge(dob: string): string | null {
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
        months--;
        const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += prevMonth.getDate();
    }
    if (months < 0) {
        years--;
        months += 12;
    }

    if (years >= 2) return `Age ${years}`;
    if (years === 1) {
        return months > 0 ? `Age 1yr ${months}mo` : "Age 1yr";
    }
    if (months >= 1) return `Age ${months}mo`;
    const totalDays = Math.floor((today.getTime() - birthDate.getTime()) / 86400000);
    if (totalDays >= 7) return `Age ${Math.floor(totalDays / 7)}wk`;
    return `Age ${totalDays}d`;
}

export function formatStudentOption(option: any, meta: { context: string }) {
    const details: string[] = [];
    if (option.dob) {
        const age = formatAge(option.dob);
        if (age) details.push(age);
    }
    if (option.enrolled_classrooms?.length) {
        details.push(option.enrolled_classrooms.join(", "));
    }
    if (meta.context === "menu" && details.length) {
        return (
            <div>
                <div>{option.label}</div>
                <div style={{ fontSize: "0.8em", color: "#888" }}>{details.join(" · ")}</div>
            </div>
        );
    }
    return option.label;
}

export function formatGuardianOption(option: any, meta: { context: string }) {
    const details: string[] = [];
    if (option.email) details.push(option.email);
    if (option.phone_no) details.push(option.phone_no);
    if (meta.context === "menu" && details.length) {
        return (
            <div>
                <div>{option.label}</div>
                <div style={{ fontSize: "0.8em", color: "#888" }}>{details.join(" · ")}</div>
            </div>
        );
    }
    return option.label;
}

export function formatTeacherOption(option: any, meta: { context: string }) {
    // Teachers: deferred enrichment (no classroom data yet)
    return option.label;
}

function mapUserToOption(user: any, userType: AccountTypeValue) {
    const label = userType === AccountType.GUARDIAN
        ? formatGuardianDisplayName(user)
        : (user.display_name ?? user.name ?? `${user.first_name} ${user.last_name}`.trim());

    return {
        value: user.uuid ?? user.id,
        label,
        dob: user.dob,
        email: user.email,
        phone_no: user.phone_no,
        enrolled_classrooms: user.enrolled_classrooms,
        first_name: user.first_name,
        last_name: user.last_name,
        display_name: user.display_name,
    };
}

export function createUserLoadOptions(userType: AccountTypeValue) {
    return async (inputValue: string) => {
        const res = await getUsers(userType, { search: inputValue || undefined, limit: 20 });
        return res.items.map((user) => mapUserToOption(user, userType));
    };
}
